import React from "react";
import { AdContextProvider } from "./AdContext";
import type { BaseAdProps } from "./AdTypes";
import { useInscreenObserver } from "./InscreenObserver";

type AdProps = Pick<
	BaseAdProps,
	| "className"
	| "currentTime"
	| "favorite"
	| "id"
	| "mode"
	| "onClick"
	| "onFirstInscreen"
	| "onMouseEnter"
	| "onMouseLeave"
	| "target"
	| "url"
> & {
	adId: string;
	children?: React.ReactNode;
	style?: React.CSSProperties;
	wrapper?: React.ElementType;
	favoriteClass?: string;
};

export function AdWrapper(props: AdProps) {
	const {
		adId,
		children,
		className = "",
		currentTime,
		favorite,
		favoriteClass,
		id,
		mode,
		onClick,
		onFirstInscreen,
		onMouseEnter,
		onMouseLeave,
		style,
		target,
		url,
		wrapper,
	} = props;

	const [inscreenRef] = useInscreenObserver({
		callback: onFirstInscreen,
		options: {
			once: true,
			threshold: 0.75,
			timeout: 1000,
		},
	});

	const headingId = `ad-${adId}`;

	const cardClass = `sf-ad card ${"card--cardShadow"} ${className}`;
	const outlineClass = "outline-none rounded-8 border-2 border-transparent sf-ad-outline";
	const favoriteWrapperClass = favoriteClass ? favoriteClass : "top-6 right-6";

	const AdWrapper = wrapper ?? "article";
	const adWrapperProps = wrapper ? { as: "article" } : {};

	return (
		<AdContextProvider
			currentTime={currentTime}
			headingId={headingId}
			linkId={id}
			mode={mode}
			onClick={onClick}
			target={target}
			url={url}
		>
			<AdWrapper
				ref={inscreenRef}
				className={`relative isolate ${cardClass}`}
				style={style}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				{...adWrapperProps}
			>
				<div className="absolute" aria-owns={headingId}></div>
				{children}
				{favorite && <span className={`absolute z-20 ${favoriteWrapperClass}`}>{favorite()}</span>}
				<div className={`absolute inset-0 pointer-events-none ${outlineClass}`}></div>
			</AdWrapper>
		</AdContextProvider>
	);
}
